import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { InstallationsContext } from 'src/contexts/InstallationsContextProvider';
import { FormattedMessage } from 'react-intl';
import { getPresetsForDevice, SODIOHOME_DEVICE_TYPES } from '../Information/installationSetupUtils';

interface SodistorehomeInstallationFormPros {
  cancel: () => void;
  submit: () => void;
  parentid: number;
  product?: number;
}

function SodistorehomeInstallationForm(props: SodistorehomeInstallationFormPros) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const isSodistorePro = props.product === 5;
  const [formValues, setFormValues] = useState<Partial<I_Installation>>({
    name: '',
    vpnIp: '',
    installationModel: '',
    externalEms: '',
    dataCollectionEnabled: true,
    ...(isSodistorePro ? { device: 4 } : {}),
  });
  const [inverterCount, setInverterCount] = useState('');
  const requiredFields = ['name', 'vpnIp', ...(isSodistorePro ? [] : ['device', 'installationModel'])];

  const DeviceTypes = isSodistorePro
    ? [{ id: 4, name: 'inesco 12K - WR Hybrid' }]
    : SODIOHOME_DEVICE_TYPES;
  const installationContext = useContext(InstallationsContext);
  const { createInstallation, loading, setLoading, error, setError } =
    installationContext;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formValues, [name]: value };

    // Reset preset when device type changes if current preset is invalid
    if (name === 'device' && !isSodistorePro) {
      const newDevicePresets = getPresetsForDevice(Number(value));
      if (formValues.installationModel && !newDevicePresets[formValues.installationModel]) {
        updated.installationModel = '';
      }
    }

    setFormValues(updated);
  };
  const handleSubmit = async (e) => {
    setLoading(true);
    formValues.parentId = props.parentid;
    formValues.product = props.product ?? 2;
    if (isSodistorePro) {
      formValues.installationModel = inverterCount;
    }
    const responseData = await createInstallation(formValues);
    props.submit();
  };
  const handleCancelSubmit = (e) => {
    props.cancel();
  };

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    if (isSodistorePro && (!inverterCount || parseInt(inverterCount, 10) < 1)) {
      return false;
    }
    return true;
  };

  const isMobile = window.innerWidth <= 1490;

  return (
    <>
      <Modal
        open={open}
        onClose={() => {}}
        aria-labelledby="error-modal"
        aria-describedby="error-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? '50%' : '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 24,
            p: 4
          }}
        >
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center', // Center items horizontally
              '& .MuiTextField-root': {
                m: 1,
                width: { xs: '100%', md: 390 }
              }
            }}
            noValidate
            autoComplete="off"
          >
            <div>
              <TextField
                label={
                  <FormattedMessage
                    id="installationName"
                    defaultMessage="Installation Name"
                  />
                }
                name="name"
                value={formValues.name}
                onChange={handleChange}
                required
                error={formValues.name === ''}
              />
            </div>
            <div>
              <TextField
                label={<FormattedMessage id="VpnIp" defaultMessage="VpnIp" />}
                name="vpnIp"
                value={formValues.vpnIp}
                onChange={handleChange}
                required
                error={formValues.vpnIp === ''}
              />
            </div>

            {isSodistorePro ? (
            <div>
              <TextField
                label={
                  <FormattedMessage
                    id="numberOfInverters"
                    defaultMessage="Number of Inverters"
                  />
                }
                name="inverterCount"
                type="text"
                value={inverterCount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) <= 20)) {
                    setInverterCount(val);
                  }
                }}
                required
                error={!inverterCount || parseInt(inverterCount, 10) < 1}
              />
            </div>
            ) : (
            <>
            {/* Device type must be selected before model — it determines available presets */}
            <div>
              <FormControl
                fullWidth
                required
                sx={{
                  marginTop: 1,
                  marginBottom: 1,
                  width: { xs: '100%', md: 390 }
                }}
              >
                <InputLabel
                  sx={{
                    fontSize: 14,
                    backgroundColor: 'white'
                  }}
                >
                  <FormattedMessage
                    id="DeviceType"
                    defaultMessage="Device Type"
                  />
                </InputLabel>
                <Select
                  name="device"
                  value={formValues.device ?? ''}
                  onChange={handleChange}
                >
                  {DeviceTypes.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div>
              <FormControl
                fullWidth
                required
                disabled={!formValues.device}
                error={formValues.installationModel === ''}
                sx={{
                  marginTop: 1,
                  marginBottom: 1,
                  width: { xs: '100%', md: 390 }
                }}
              >
                <InputLabel
                  sx={{
                    fontSize: 14,
                    backgroundColor: 'white'
                  }}
                >
                  <FormattedMessage
                    id="installationModel"
                    defaultMessage="Installation Model"
                  />
                </InputLabel>
                <Select
                  name="installationModel"
                  value={formValues.installationModel || ''}
                  onChange={handleChange}
                >
                  {Object.keys(getPresetsForDevice(formValues.device as number)).map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            </>
            )}

            <div>
              <FormControl
                fullWidth
                sx={{
                  marginTop: 1,
                  marginBottom: 1,
                  width: { xs: '100%', md: 390 }
                }}
              >
                <InputLabel
                  sx={{
                    fontSize: 14,
                    backgroundColor: 'white'
                  }}
                >
                  <FormattedMessage
                    id="dataCollectionEnabled"
                    defaultMessage="Data Collection"
                  />
                </InputLabel>
                <Select
                  name="dataCollectionEnabled"
                  value={formValues.dataCollectionEnabled ? 'yes' : 'no'}
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      dataCollectionEnabled: e.target.value === 'yes'
                    })
                  }
                >
                  <MenuItem value="yes">
                    <FormattedMessage id="yes" defaultMessage="Yes" />
                  </MenuItem>
                  <MenuItem value="no">
                    <FormattedMessage id="no" defaultMessage="No" />
                  </MenuItem>
                </Select>
              </FormControl>
            </div>

          </Box>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 10
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                marginLeft: '20px'
              }}
              disabled={!areRequiredFieldsFilled()}
            >
              <FormattedMessage id="submit" defaultMessage="Submit" />
            </Button>

            <Button
              variant="contained"
              onClick={handleCancelSubmit}
              sx={{
                marginLeft: '10px'
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>

            {loading && (
              <CircularProgress
                sx={{
                  color: theme.palette.primary.main,
                  marginLeft: '20px'
                }}
              />
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FormattedMessage
                  id="errorOccured"
                  defaultMessage="An error has occured"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setError(false)}
                  sx={{ marginLeft: '4px' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Alert>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default SodistorehomeInstallationForm;
